import { Location } from '@angular/common';
import { Inject, Injectable, InjectionToken, ValueProvider } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';

export interface RoutingServiceConfig {
  domain: string;
  cookieDomain: string;
}

export interface QueryParams {
  [id: string]: any;
}

export const ROUTING_SERVICE_CONFIG = new InjectionToken<RoutingServiceConfig>('RoutingServiceConfig');

const STORAGE_KEY = 'ute.rci.deep.link.url';
const matchOperatorsRegex = /[|\\{}()[\]^$+*?.-]/g;

@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  url$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => this.router.routerState.snapshot.url),
  );

  constructor(
    @Inject(ROUTING_SERVICE_CONFIG) private config: RoutingServiceConfig,
    private router: Router,
    private location: Location,
  ) {}

  static configure(config?: RoutingServiceConfig): ValueProvider {
    return {
      provide: ROUTING_SERVICE_CONFIG,
      useValue: config || {},
    };
  }

  getCurrentUrl() {
    return this.router.url;
  }

  navigate(url: string, queryParams?: QueryParams, state?: { [k: string]: any }) {
    const loginRedirectUrl = url.match(new RegExp('[?&]loginRedirect=([^&]+)'));
    if (loginRedirectUrl) {
      sessionStorage.setItem(STORAGE_KEY, loginRedirectUrl[1]);
      return window.open(url, '_self');
    }
    if (this.isInternalRoutePath(url)) {
      return this.router.navigate([url], { queryParams, state });
    }
    return window.open(url, this.isInternalUrl(url) ? '_self' : '_blank');
  }

  getUrlQueryParameter(name) {
    const match = this.location.path(false).match(new RegExp(`[\?&]${name}=([^&]+)`));
    if (match) {
      return match[1];
    }
    return '';
  }

  reloadCurrentRoute() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  private isInternalRoutePath(url: string): boolean {
    return !url.startsWith('/') && url.indexOf('//') === -1;
  }

  // noinspection JSMethodCanBeStatic
  private isInternalUrl(url) {
    return (
      url.startsWith('/') ||
      new RegExp(`^(https?:\/\/)?([^/]+\.)?${this.escapeStringRegexp(this.config.domain)}`, 'i').test(url)
    );
  }

  private escapeStringRegexp(text): string {
    if (typeof text !== 'string') {
      throw new TypeError('Expected a string');
    }

    return text.replace(matchOperatorsRegex, '\\$&');
  }
}
